import React from 'react'
import { useIntl } from 'react-intl'

import { BasicListItem } from './basic-list-item.component'

/**
 * @typedef {object} BasicListField
 * @property {string} key - La clave del campo en el objeto data.
 * @property {string} label - La etiqueta que se muestra para este campo.
 * @property {'combo' | 'date' | 'currency' | 'file' | 'default'} [type] - Tipo de dato para formateo especial.
 * @property {string} [comboId] - ID del combo en el objeto combos (requerido cuando type='combo').
 * @property {boolean} [multiple] - Si es true, el campo combo puede tener múltiples valores seleccionados.
 * @property {string} [currency] - Código de moneda para formateo (ej: 'EUR', 'USD') cuando type='currency'.
 */

/**
 * @typedef {object} BasicListProps
 * @property {object} data - Objeto con los datos a mostrar en la lista.
 * @property {BasicListField[]} fields - Array de configuraciones de campos a renderizar.
 * @property {Object.<string, {data: Array<{key: any, value: string}>}>} [combos] - Objeto con arrays de opciones para campos tipo combo.
 */

/**
 * Un componente de lista de definición que muestra pares clave-valor con formateo automático.
 *
 * Renderiza una lista de campos con sus valores formateados según el tipo especificado.
 * Soporta formateo automático de fechas, monedas, archivos, y valores de combos (incluyendo
 * selección múltiple). Solo muestra los campos que tienen un valor en el objeto data.
 *
 * @component
 * @example
 * const MyComponent = () => {
 *   const data = {
 *     nombre: 'Juan Pérez',
 *     fechaNacimiento: '1990-05-15',
 *     pais: 'ES',
 *     precio: 1500.50,
 *     intereses: ['deportes', 'musica']
 *   };
 *
 *   const combos = {
 *     paises: {
 *       data: [
 *         { key: 'ES', value: 'España' },
 *         { key: 'FR', value: 'Francia' }
 *       ]
 *     },
 *     intereses: {
 *       data: [
 *         { key: 'deportes', value: 'Deportes' },
 *         { key: 'musica', value: 'Música' }
 *       ]
 *     }
 *   };
 *
 *   const fields = [
 *     { key: 'nombre', label: 'Nombre' },
 *     { key: 'fechaNacimiento', label: 'Fecha de Nacimiento', type: 'date' },
 *     { key: 'pais', label: 'País', type: 'combo', comboId: 'paises' },
 *     { key: 'precio', label: 'Precio', type: 'currency', currency: 'EUR' },
 *     { key: 'intereses', label: 'Intereses', type: 'combo', comboId: 'intereses', multiple: true }
 *   ];
 *
 *   return <BasicList data={data} fields={fields} combos={combos} />;
 * };
 *
 * @param {BasicListProps} props - Las props del componente BasicList.
 */
export const BasicList = ({ data, fields, combos }) => {
  const { formatDate, formatNumber } = useIntl()

  const getValueByType = (value, field, combos) => {
    switch (field.type) {
      case 'combo': {
        if (field.comboId && combos[field.comboId]?.data) {
          if (field.multiple) {
            let selectedOptionsValues = []
            value.forEach((key) => {
              const selectedOption = combos[field.comboId].data.find((option) => option.key === key)
              if (selectedOption) selectedOptionsValues.push(selectedOption.value)
            })
            return selectedOptionsValues.join(', ')
          } else {
            const selectedOption = combos[field.comboId].data.find((option) => option.key === value)
            if (selectedOption) {
              return selectedOption.value
            }
          }
        }
        break
      }
      case 'date': {
        return formatDate(new Date(value))
      }
      case 'currency': {
        return formatNumber(value, { style: 'currency', currency: field.currency })
      }
      case 'file': {
        return value.name
      }
      default:
        return value
    }
  }

  return fields.map((field) => {
    const value = data[field.key]
    return value ? (
      <BasicListItem
        {...{ key: field.key, label: field.label, value: getValueByType(value, field, combos) }}
      />
    ) : null
  })
}
